import { nanoid } from "nanoid";
import apiClient from "./api-client";

export type ShareChannel =
  | "linkedin"
  | "x"
  | "facebook"
  | "whatsapp"
  | "telegram"
  | "email"
  | "copy"
  | "embed"
  | "native";

const REFERRAL_COOKIE = "ctlz_ref";
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Map ShareButton `type` prop to backend ContributionType. */
const ENTITY_TYPE_MAP: Record<string, string> = {
  project: "PROJECT",
  portfolio: "PORTFOLIO",
  challenge: "CHALLENGE",
  researcher: "USER",
};

/** Map ShareButton `platform` to ShareChannel. */
const PLATFORM_TO_CHANNEL: Record<string, ShareChannel> = {
  twitter: "x",
  telegram: "telegram",
  whatsapp: "whatsapp",
  facebook: "facebook",
  linkedin: "linkedin",
  mail: "email",
  copy: "copy",
};

export function platformToChannel(platform: string): ShareChannel {
  return PLATFORM_TO_CHANNEL[platform] ?? "copy";
}

/**
 * Mints a ShareLink (fire-and-forget POST) and returns the share URL
 * synchronously. The URL is returned before persistence completes on purpose:
 * callers feed it straight into `navigator.share()` / `window.open()`, which
 * require the synchronous user activation that `await`-ing the request would
 * consume. Persistence is therefore best-effort — but failures are logged
 * (not silently swallowed) so an un-tracked share link is observable rather
 * than invisible. Returns a plain URL when the entity type has no backend
 * mapping (e.g. "post").
 */
export function createShareUrl(
  path: string,
  entity: { type: string; id: string },
  channel: ShareChannel,
): string {
  const backendType = ENTITY_TYPE_MAP[entity.type];
  if (!backendType) {
    return new URL(path, window.location.origin).toString();
  }

  const id = nanoid(10);
  const url = new URL(path, window.location.origin);
  url.searchParams.set("s", id);

  void apiClient
    .post("/shares", {
      id,
      entityType: backendType,
      entityId: entity.id,
      channel,
    })
    .catch((err) => {
      console.error(
        `[referral] failed to persist share ${id} (${backendType}:${entity.id}); link will be untracked`,
        err,
      );
    });

  return url.toString();
}

/** Derive the actual traffic source from `document.referrer`. */
export function deriveSource(): string {
  try {
    const ref = document.referrer;
    if (!ref) return "direct";
    const host = new URL(ref).hostname;
    if (host.includes("linkedin.com")) return "linkedin";
    if (host.includes("x.com") || host.includes("twitter.com")) return "x";
    if (host.includes("facebook.com") || host.includes("fb.com"))
      return "facebook";
    if (host.includes("whatsapp.com") || host.includes("wa.me"))
      return "whatsapp";
    if (host.includes("t.me") || host.includes("telegram.org"))
      return "telegram";
    return "direct";
  } catch {
    return "direct";
  }
}

/** Write referral data to a 30-day cookie. */
export function storeReferral(shareId: string, source: string): void {
  const value = JSON.stringify({ shareId, source, ts: Date.now() });
  const expires = new Date(Date.now() + REFERRAL_TTL_MS).toUTCString();
  document.cookie = `${REFERRAL_COOKIE}=${encodeURIComponent(value)}; path=/; expires=${expires}; SameSite=Lax`;
}

/** Read and de-expire the referral cookie (last-touch attribution). */
export function getStoredReferral(): {
  shareId: string;
  source: string;
} | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${REFERRAL_COOKIE}=([^;]*)`),
  );
  if (!match?.[1]) return null;
  try {
    const data = JSON.parse(decodeURIComponent(match[1])) as {
      shareId: string;
      source: string;
      ts: number;
    };
    if (Date.now() - data.ts > REFERRAL_TTL_MS) {
      document.cookie = `${REFERRAL_COOKIE}=; path=/; max-age=0`;
      return null;
    }
    return { shareId: data.shareId, source: data.source };
  } catch {
    return null;
  }
}
